import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ObjectActions from './activity-logs.actions';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as AppActions from '../../app/actions';
import { Store } from '@ngrx/store';
import * as oeeAppReducer from '../../oee.reducer';
import { ActivityLogsService } from './activity-logs.service';
import { BaseOneResponseInterface } from '../../../shared/model/interface/crud-response-interface.model';
import { ActivityLogInterface, ActivityLogReportInterface } from './activity-logs.model';
import { EntityTranslatorService } from '../../../shared/service/entity-translator/entity-translator.service';
import { emptyAction } from '../../../../constants';
import { HelperService } from '../../../shared/service/helper.service';
import { HttpParams } from '@angular/common/http';
import { LineService } from '../../../shared/service/filter/line.service';
import { LineCRUDResponseInterface } from '../../../shared/service/filter/service.class';
import { CodeAndNameDisplayService } from '../../../shared/service/code-and-name-display/code-and-name-display.service';

@Injectable()
export class ActivityLogsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: ActivityLogsService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly entityTranslatorService: EntityTranslatorService,
    private readonly lineService: LineService,
    private readonly codeAndNameDisplayService: CodeAndNameDisplayService,
  ) {}

  getActivityLogData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.ACTIVITY_LOGS_DATA_LOADING),
      switchMap((payload: ObjectActions.ActivityLogsDataLoading) => {
        const data = this.service.formatActivityLogParams(payload.params);

        return this.service.getActivityLogs(data).pipe(
          switchMap((response: BaseOneResponseInterface<ActivityLogReportInterface>) => {
            response.data.rows = response.data.rows.map((activityLog: ActivityLogInterface) => {
              this.entityTranslatorService.translate(activityLog);
              activityLog.activityName = this.codeAndNameDisplayService.getDisplay({
                name: activityLog.activityName,
                code: activityLog.activityCode,
              });
              activityLog.taskName = this.codeAndNameDisplayService.getDisplay({
                name: activityLog.taskName,
                code: activityLog.taskCode,
              });
              this.service.setActivityLogProductInfo(activityLog);
              this.service.setActivityLogTaskName(activityLog);

              return {
                ...activityLog,
                isNotEditable: activityLog.end === 'Ongoing',
              };
            });
            return of(new ObjectActions.ActivityLogsDataLoaded(response));
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes));
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes));
      }),
    ),
  );

  downloadActivityExcel = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.ACTIVITY_LOGS_DOWNLOAD_EXCEL),
      switchMap((objectData: ObjectActions.ActivityLogsDownloadExcel) => {
        this.service.downloadExcel(
          HelperService.cloneDeep(objectData.params),
          HelperService.cloneDeep(objectData.worksheetsColumnDefinitions),
          objectData.fileDownloadType === 'csv',
        );
        return emptyAction;
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  exportActivityLogs = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.EXPORT_ACTIVITY_LOGS),
      switchMap((payload: ObjectActions.ExportActivityLogs) => {
        this.store.dispatch(new AppActions.ShowLoader());
        const formattedParams = this.service.formatActivityLogParams(payload.params);

        return this.service
          .exportActivityLogs({
            ...formattedParams,
            ...payload.exportRequestData,
          })
          .pipe(
            switchMap(() => {
              return of(new ObjectActions.ExportActivityLogsCompleted(), new AppActions.HideLoader());
            }),
            catchError((error) => {
              return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((error) => {
        return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  getLines = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.GET_LINES),
      switchMap((objectData: ObjectActions.GetLines) => {
        let httpParams: HttpParams = new HttpParams()
          .set('fields', 'title,isOpcIntegrationInterrupted')
          .set('limit', '500')
          .set('sort', 'isOpcIntegrationInterrupted,DESC');
        const searchObject = {
          isOpcIntegrationInterrupted: { $notnull: true },
          status: 1,
        };

        if (objectData.params.sites !== -1) {
          searchObject['siteId'] = { $in: objectData.params.sites };
        }

        if (objectData.params.lines !== -1) {
          searchObject['id'] = { $in: objectData.params.lines };
        }

        httpParams = httpParams.set('s', JSON.stringify(searchObject));

        return this.lineService.getLines(httpParams).pipe(
          switchMap((response: LineCRUDResponseInterface) => {
            return of(new ObjectActions.GetLinesCompleted(response.data));
          }),
          catchError((error) => {
            return of(new ObjectActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new ObjectActions.FetchError(error));
      }),
    ),
  );
}
